import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Container from '../components/common/Container';
import { ImagePreview } from '../components/common/ImageModal';
import RealTimeInsightPreview from '../components/home/RealTimeInsightPreview';
import {
  AcademicCapIcon,
  ChartBarIcon,
  UserGroupIcon,
  SparklesIcon,
  ClockIcon,
  ChartPieIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  BeakerIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';

const Home: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      {/* Hero Section - Premium Dark */}
      <section className="relative min-h-screen flex items-center animated-gradient-bg overflow-hidden">
        {/* Background screenshot hints - faded */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="/student_learning_view_large.jpg"
            alt=""
            aria-hidden="true"
            className="absolute -left-20 top-1/4 w-[500px] opacity-[0.15] rotate-[-8deg] pointer-events-none select-none"
          />
          <img
            src="/customisable_lessons.jpg"
            alt=""
            aria-hidden="true"
            className="absolute -right-20 top-1/3 w-[550px] opacity-[0.15] rotate-[6deg] pointer-events-none select-none"
          />
          <img
            src="/student_insights_large.jpg"
            alt=""
            aria-hidden="true"
            className="absolute left-1/4 -bottom-20 w-[500px] opacity-[0.12] rotate-[3deg] pointer-events-none select-none"
          />
        </div>

        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--color-electric-blue)] rounded-full filter blur-[128px] opacity-20 float-animation"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[var(--color-violet)] rounded-full filter blur-[128px] opacity-20 float-animation" style={{ animationDelay: '2s' }}></div>
        </div>

        <Container size="xl" className="relative z-10">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Premium badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block mb-6"
            >
              <div className="glass-card px-4 py-2 rounded-full border border-[var(--color-electric-blue)]/30">
                <span className="text-sm font-semibold text-[var(--color-electric-blue)]">
                  AI-Powered Adaptive Learning for K-12
                </span>
              </div>
            </motion.div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black mb-8 leading-[0.9]">
              <span className="text-white">The Future of</span>
              <br />
              <span className="text-gradient">Adaptive Learning</span>
              <br />
              <span className="text-white">is Here</span>
            </h1>

            <p className="text-xl md:text-2xl text-[var(--color-light-gray)] mb-12 leading-relaxed max-w-3xl mx-auto">
              <span className="text-[var(--color-electric-blue)] font-semibold">Maths & English</span> with more subjects coming soon —
              real-time mastery tracking that transforms quarterly report cards into continuous insight.
              AI-powered education aligned to the NSW curriculum.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2 hover-glow">
                  Schedule Demo
                  <ArrowTrendingUpIcon className="h-5 w-5" />
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4 inline-flex items-center gap-2">
                  Watch Platform Tour
                  <ChartPieIcon className="h-5 w-5" />
                </Link>
              </motion.div>
            </div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={heroInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-16 flex flex-wrap justify-center gap-8"
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                <ShieldCheckIcon className="h-5 w-5 text-[var(--color-emerald)]" />
                <span className="text-sm font-medium text-[var(--color-emerald)]">Enterprise Security</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
                <AcademicCapIcon className="h-5 w-5 text-[var(--color-violet)]" />
                <span className="text-sm font-medium text-[var(--color-violet)]">NSW Curriculum Aligned</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                <BeakerIcon className="h-5 w-5 text-[var(--color-electric-blue)]" />
                <span className="text-sm font-medium text-[var(--color-electric-blue)]">Evidence-Based Learning</span>
              </div>
            </motion.div>
          </motion.div>
        </Container>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-[var(--color-medium-gray)]"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </div>
      </section>

      {/* Platform Overview Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            ref={statsRef}
            initial={{ opacity: 0 }}
            animate={statsInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              An AI-Powered <span className="text-gradient">Adaptive Learning Platform</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {[
                { text: 'Curriculum-aligned educational content for K-12 students across multiple subjects' },
                { text: 'Real-time progress tracking and mastery assessment' },
                { text: 'Adaptive learning paths based on individual performance' },
                { text: 'Teacher dashboards and classroom management tools' },
                { text: 'Parent portals for monitoring student progress' },
                { text: 'Gamification features to enhance engagement' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[var(--color-electric-blue)] mt-2"></div>
                  <p className="text-[var(--color-light-gray)]">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Challenges Section - Collaborative Approach */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-black mb-8">
                <span className="text-white">Traditional Education's</span>
                <br />
                <span className="text-gradient">Challenges</span>
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[var(--color-amber)]/20 flex items-center justify-center">
                    <ClockIcon className="h-6 w-6 text-[var(--color-amber)]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Feedback Cycles</h3>
                    <p className="text-[var(--color-light-gray)]">
                      Traditional reporting cycles mean learning insights often arrive months after the opportunity for intervention.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[var(--color-violet)]/20 flex items-center justify-center">
                    <CubeTransparentIcon className="h-6 w-6 text-[var(--color-violet)]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Fragmented Systems</h3>
                    <p className="text-[var(--color-light-gray)]">
                      Educators often work across multiple platforms, making it harder to get a unified view of student progress.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[var(--color-electric-blue)]/20 flex items-center justify-center">
                    <ChartBarIcon className="h-6 w-6 text-[var(--color-electric-blue)]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Mastery Insights</h3>
                    <p className="text-[var(--color-light-gray)]">
                      Understanding exactly where each student stands across curriculum outcomes can be time-consuming to track.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <RealTimeInsightPreview />
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[var(--color-electric-blue)] rounded-full filter blur-3xl opacity-30"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[var(--color-violet)] rounded-full filter blur-3xl opacity-30"></div>
            </div>
          </div>
        </Container>
      </section>

      {/* Solution - Premium Features */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              Introducing <span className="text-gradient">Lumen8</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)]">
              The adaptive learning platform that makes every moment of learning visible, measurable, and actionable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BeakerIcon,
                title: 'Maths & English',
                description: 'Start with Mathematics and English — with Science and more subjects coming soon. One platform that grows with your curriculum needs.',
                gradient: 'from-[var(--color-emerald)] to-[var(--color-electric-blue)]'
              },
              {
                icon: ChartPieIcon,
                title: 'Live Knowledge Graph',
                description: 'Visual mastery tracking across every curriculum node, updated in real-time with each interaction.',
                gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]'
              },
              {
                icon: SparklesIcon,
                title: 'AI-Powered Adaptation',
                description: 'Intelligent algorithms that adjust difficulty, pacing, and content based on individual performance patterns.',
                gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]'
              },
              {
                icon: UserGroupIcon,
                title: 'Unified Experience',
                description: 'One platform with role-specific dashboards for students, teachers, parents, and administrators.',
                gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]'
              },
              {
                icon: ClockIcon,
                title: 'Instant Feedback',
                description: 'Replace quarterly reports with continuous insight. See progress as it happens, not months later.',
                gradient: 'from-[var(--color-rose)] to-[var(--color-violet)]'
              },
              {
                icon: AcademicCapIcon,
                title: 'Curriculum Aligned',
                description: 'Fully mapped to Australian education standards with complete coverage from Kindergarten to Year 12.',
                gradient: 'from-[var(--color-bright-cyan)] to-[var(--color-emerald)]'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="card-premium p-8 h-full hover-glow transition-all duration-300 hover:scale-[1.02]">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} p-3 mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[var(--color-light-gray)]">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Platform Preview Section */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              See <span className="text-gradient">Lumen8</span> in Action
            </h2>
            <p className="text-xl text-[var(--color-light-gray)]">
              Experience the platform through the eyes of students, teachers, and parents.
            </p>
          </div>

          {/* Screenshot Placeholders - Replace with actual screenshots */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="card-premium overflow-hidden hover-glow"
            >
              <ImagePreview
                title="Student Learning View"
                description="Gamified lessons with real-time progress tracking"
                imageAlt="Student Dashboard"
                imageSrc="/student_learning_view_large.jpg"
              >
                <div className="aspect-video relative overflow-hidden bg-[var(--color-midnight)]">
                  <img
                    src="/student_learning_view_small.png"
                    alt="Student Learning View"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                      <span className="text-sm font-medium">Click to enlarge</span>
                    </div>
                  </div>
                </div>
              </ImagePreview>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white">Student Learning View</h3>
                <p className="text-sm text-[var(--color-light-gray)]">Gamified lessons with real-time progress tracking</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="card-premium overflow-hidden hover-glow"
            >
              <ImagePreview
                title="Student Insights"
                description="Deep dive into individual student progress and mastery"
                imageAlt="Student Insights Dashboard"
                imageSrc="/student_insights_large.jpg"
              >
                <div className="aspect-video relative overflow-hidden bg-[var(--color-midnight)]">
                  <img
                    src="/classroom_insights_small.png"
                    alt="Student Insights"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                      <span className="text-sm font-medium">Click to enlarge</span>
                    </div>
                  </div>
                </div>
              </ImagePreview>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white">Student Insights</h3>
                <p className="text-sm text-[var(--color-light-gray)]">Deep dive into individual student progress and mastery</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="card-premium overflow-hidden hover-glow"
            >
              <ImagePreview
                title="Customisable Lesson Plans"
                description="Build and organise curriculum-aligned learning pathways"
                imageAlt="Course Outline Builder"
                imageSrc="/customisable_lessons.jpg"
              >
                <div className="aspect-video relative overflow-hidden bg-[var(--color-midnight)]">
                  <img
                    src="/customisable_lesson_plan.png"
                    alt="Customisable Lesson Plans"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                      <span className="text-sm font-medium">Click to enlarge</span>
                    </div>
                  </div>
                </div>
              </ImagePreview>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white">Customisable Lesson Plans</h3>
                <p className="text-sm text-[var(--color-light-gray)]">Build and organise curriculum-aligned learning pathways</p>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Evidence Based Approach Section */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              An <span className="text-gradient">Evidence Based</span> Approach
            </h2>
            <p className="text-xl text-[var(--color-light-gray)]">
              Lumen8 is built on proven educational research that benefits everyone in the learning ecosystem.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* For Students */}
            <div className="card-premium p-8 hover-glow">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-3 mb-6">
                <AcademicCapIcon className="w-full h-full text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">For Students</h3>
              <div className="text-3xl font-black text-gradient mb-2">2 Sigma Effect</div>
              <p className="text-[var(--color-light-gray)]">
                Research shows personalised learning with mastery-based progression can improve student outcomes by 2 standard deviations (Bloom, 1984).
              </p>
            </div>
            {/* For Teachers */}
            <div className="card-premium p-8 hover-glow">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-3 mb-6">
                <UserGroupIcon className="w-full h-full text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">For Teachers</h3>
              <div className="text-3xl font-black text-gradient mb-2">Actionable Data</div>
              <p className="text-[var(--color-light-gray)]">
                Formative assessment and real-time feedback loops allow teachers to identify and address learning gaps immediately (Hattie, 2009).
              </p>
            </div>
            {/* For Parents */}
            <div className="card-premium p-8 hover-glow">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-3 mb-6">
                <ChartBarIcon className="w-full h-full text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">For Parents</h3>
              <div className="text-3xl font-black text-gradient mb-2">Visible Progress</div>
              <p className="text-[var(--color-light-gray)]">
                Parent involvement in education is linked to improved academic performance, better attendance, and higher graduation rates (Henderson & Mapp, 2002).
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section - Premium */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] via-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
              Join the Education
              <span className="text-gradient"> Revolution</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-12">
              Leading schools are already transforming their educational outcomes with Lumen8.
              Don't let your students fall behind.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
              <Link to="/contact" className="btn-premium text-lg px-10 py-4 inline-flex items-center gap-2 pulse-glow">
                Schedule Your Demo
                <ArrowTrendingUpIcon className="h-5 w-5" />
              </Link>
              <Link to="/features" className="btn-glass text-lg px-10 py-4">
                Explore Features
              </Link>
            </div>
            <p className="text-sm text-[var(--color-medium-gray)]">
              NSW Curriculum Aligned • Maths & English (more coming soon) • Real-Time Mastery Tracking
            </p>
          </div>
        </Container>
      </section>
    </>
  );
};

export default Home;